import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingIndicator } from '../../components/Spinner';
import { formatDateTime } from '../../i18n';
import type { ActivityType, Contact } from './api';
import { useCreateActivity, useCustomerTimeline, useDeleteActivity } from './queries';
import { Select } from '../../components/Select';

interface ActivityPanelProps {
  customerId: string;
  contacts: Contact[];
  canWrite: boolean;
}

const TYPES: readonly ActivityType[] = ['call', 'meeting', 'email', 'note'];

/**
 * Aktivitaeten am Kunden -- F2-07.
 *
 * Was den Kunden betrifft, aber zu keinem Ticket gehoert: ein Anruf zur
 * Vertragsfrage, eine Besprechung, ein Vermerk.
 *
 * **Der Zeitpunkt ist der des Vorgangs, nicht der der Eingabe.** Ein Anruf von
 * gestern, heute vermerkt, gehoert in der Historie an die Stelle von gestern
 * -- sonst liest sich der Verlauf falsch herum.
 */
export function ActivityPanel({ customerId, contacts, canWrite }: ActivityPanelProps) {
  const { t, i18n } = useTranslation();
  const timeline = useCustomerTimeline(customerId);
  const create = useCreateActivity(customerId);
  const remove = useDeleteActivity();

  const [values, setValues] = useState({
    activity_type: 'call' as ActivityType,
    subject: '',
    body: '',
    contact_id: '',
    occurred_at: '',
  });

  const set = <K extends keyof typeof values>(field: K, value: (typeof values)[K]) => {
    setValues((current) => ({ ...current, [field]: value }));
  };

  const activities = (timeline.data ?? []).filter((entry) => entry.source === 'activity');

  return (
    <div className="stack">
      {canWrite ? (
        <section className="card">
          <h2>{t('crm.newActivity')}</h2>

          <div className="row">
            {TYPES.map((value) => (
              <Button
                key={value}
                variant={values.activity_type === value ? 'secondary' : 'ghost'}
                aria-pressed={values.activity_type === value}
                onClick={() => {
                  set('activity_type', value);
                }}
              >
                {t(`channel.${value}`)}
              </Button>
            ))}
          </div>

          <div className="field">
            <label className="field__label" htmlFor="activity-subject">
              {t('crm.activitySubject')}
            </label>
            <input
              id="activity-subject"
              className="field__control"
              value={values.subject}
              onChange={(event) => {
                set('subject', event.target.value);
              }}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="activity-body">
              {t('crm.activityBody')}
            </label>
            <textarea
              id="activity-body"
              className="field__control field__control--area"
              rows={4}
              value={values.body}
              onChange={(event) => {
                set('body', event.target.value);
              }}
            />
          </div>

          <div className="row">
            <div className="field">
              <label className="field__label" htmlFor="activity-when">
                {t('crm.occurredAt')}
              </label>
              <input
                id="activity-when"
                type="datetime-local"
                className="field__control"
                value={values.occurred_at}
                onChange={(event) => {
                  set('occurred_at', event.target.value);
                }}
              />
              <span className="field__hint">{t('crm.occurredAtHint')}</span>
            </div>

            {contacts.length > 0 ? (
              <div className="field">
                <label className="field__label" htmlFor="activity-contact">
                  {t('crm.contact')}
                </label>
                <Select
                  id="activity-contact"
                  className="field__control"
                  value={values.contact_id}
                  onChange={(event) => {
                    set('contact_id', event.target.value);
                  }}
                >
                  <option value="">{t('crm.noContact')}</option>
                  {contacts.map((contact) => (
                    <option key={contact.id} value={contact.id}>
                      {contact.first_name} {contact.last_name}
                    </option>
                  ))}
                </Select>
              </div>
            ) : null}
          </div>

          {create.isError ? <ErrorNotice error={create.error} /> : null}

          <Button
            variant="primary"
            disabled={!values.subject.trim()}
            busy={create.isPending}
            onClick={() => {
              create.mutate(
                {
                  activity_type: values.activity_type,
                  subject: values.subject.trim(),
                  body: values.body,
                  ...(values.contact_id ? { contact_id: values.contact_id } : {}),
                  // Ohne Angabe entscheidet der Server: dann gilt jetzt.
                  ...(values.occurred_at
                    ? { occurred_at: new Date(values.occurred_at).toISOString() }
                    : {}),
                },
                {
                  onSuccess: () => {
                    setValues({
                      activity_type: 'call',
                      subject: '',
                      body: '',
                      contact_id: '',
                      occurred_at: '',
                    });
                  },
                },
              );
            }}
          >
            {t('masterdata.add')}
          </Button>
        </section>
      ) : null}

      {timeline.isPending ? <LoadingIndicator /> : null}
      {timeline.isError ? <ErrorNotice error={timeline.error} /> : null}
      {remove.isError ? <ErrorNotice error={remove.error} /> : null}

      {activities.length === 0 && !timeline.isPending ? (
        <EmptyState icon="crm" title={t('crm.noActivities')} hint={t('crm.noActivitiesHint')} />
      ) : null}

      <ol className="timeline">
        {activities.map((entry) => (
          <li key={entry.id} className="timeline__entry">
            <div className="timeline__head">
              <Badge tone="info">
                {t(`channel.${entry.channel}`, { defaultValue: entry.channel })}
              </Badge>
              <span className="timeline__time">
                {formatDateTime(entry.occurred_at, i18n.language)}
              </span>
              {canWrite ? (
                <Button
                  variant="ghost"
                  onClick={() => {
                    remove.mutate(entry.id);
                  }}
                >
                  {t('masterdata.delete')}
                </Button>
              ) : null}
            </div>
            {entry.subject ? <p className="timeline__subject">{entry.subject}</p> : null}
            <p className="prose">{entry.excerpt}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}
